import { useState, useEffect } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { useToast } from '@/context/ToastContext';
import Edit from './Edit';
import DeletedHistory from '../DeletedHistory';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import { TourBagService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { TourBagDetailAddNew } from '@/db/models';
import { TourBagDetail, TourBagMstrPayload } from '@/api/models';
import { HeadTypeDescription } from '@/api/models/TourBagMstrPayload';
import { ITourPageSettings } from '@/library/TourPageSettingsHelper';
import { isFieldVisible } from '@/library/FieldVisibilityHelper';
import { PageReadability } from '@/library/TourPageSettingsHelper';
import { createDeletedTourBag, createEmptyTourBag } from '@/utils';
import { headTypeMap, Action } from '@/constants';
import clubStyles from '../Club.module.scss';
import driverStyles from './Metals.module.scss';
import { Accordion, ControlledAccordion } from '@/common/Accordion';
import InTheBag from '@/common/InTheBag';
import Tile from '@/common/Tile';
import { isEmptyTile, normalizeString } from '@/utils';
import ClubHistory from '../ClubHistory/ClubHistory';
import OfflineModal from '@/common/OfflineModal';
import Linkify from 'linkify-react';
import PhotoCaptureSection from '@/common/PhotoCaptureSection';

const renderMetal = (metal: TourBagDetail, headTypeId: number, pageReadability: PageReadability | undefined) => {
  const notes = normalizeString(metal.notes);
  const showWeightAft = ['Driver', 'Fairway'].includes(metal.headTypeDescription);
  const module = metal.headTypeDescription;

  return (
    <>
      <div className={clubStyles.tileRow}>
        {isFieldVisible(module, 'familyName', pageReadability) && <Tile label="Family" value={normalizeString(metal.familyName)} variant="large" />}
        {isFieldVisible(module, 'hand', pageReadability) && <Tile label="Hand" value={normalizeString(metal.hand)} />}
        {isFieldVisible(module, 'loftName', pageReadability) && <Tile label="Loft" value={normalizeString(metal.loftName)} />}
        {isFieldVisible(module, 'measuredLoft', pageReadability) && <Tile label="Msrd. Loft" value={normalizeString(metal.measuredLoft)} />}
        {isFieldVisible(module, 'headWeightLocationLookupName', pageReadability) && <Tile label="Fwd/Toe Wt. Loc." value={normalizeString(metal.headWeightLocationLookupName)} variant="large" />}
        {showWeightAft && isFieldVisible(module, 'headWeightLocationAftLookupName', pageReadability) && (
          <Tile label="Aft/Heel Wt. Loc." value={normalizeString(metal.headWeightLocationAftLookupName)} variant="large" />
        )}
        {headTypeId === 1 && (
          <>
            {isFieldVisible(module, 'ct', pageReadability) && <Tile label="CT" value={normalizeString(metal.ct)} />}
            {isFieldVisible(module, 'ctLocName', pageReadability) && <Tile label="CT Loc." value={normalizeString(metal.ctLocation)} />}
          </>
        )}
        {isFieldVisible(module, 'swingWt', pageReadability) && <Tile label="Swing Wt." value={normalizeString(`${metal.swingWtPrefixName} ${metal.swingWtSuffixName}`)} />}
        {isFieldVisible(module, 'headWeight', pageReadability) && <Tile label="Final HW" value={normalizeString(metal.headWeight?.toString())} />}
        {isFieldVisible(module, 'headWeightNumberLookupName', pageReadability) && <Tile label="Fwd/Toe Wt. #" value={normalizeString(metal.headWeightNumberLookupName)} variant="large" />}
        {showWeightAft && isFieldVisible(module, 'headWeightNumberAftLookupName', pageReadability) && (
          <Tile label="Aft/Heel Wt. #" value={normalizeString(metal.headWeightNumberAftLookupName)} variant="large" />
        )}
      </div>
      {isFieldVisible(module, 'ratGlue1', pageReadability) && (
        <div className={clubStyles.tileRow}>
          <Tile label="Hot Melt 1" value={normalizeString(metal.ratGlue1)} variant="large" />
          <Tile label="Hot Melt 2" value={normalizeString(metal.ratGlue2)} variant="large" />
          <Tile label="Hot Melt 3" value={normalizeString(metal.ratGlue3)} variant="large" />
          <Tile label="Hot Melt 4" value={normalizeString(metal.ratGlue4)} variant="large" />
          <Tile label="Hot Melt 5" value={normalizeString(metal.ratGlue5)} variant="large" />
          <Tile label="Hot Melt 6" value={normalizeString(metal.ratGlue6)} variant="large" />
          <Tile label="Hot Melt 7" value={normalizeString(metal.ratGlue7)} variant="large" />
        </div>
      )}
      {isFieldVisible(module, 'shaftName', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Shaft</div>
            <div className={clubStyles.categoryTitle}>{metal.shaftName}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'shaftLengthDigit', pageReadability) && <Tile label="Length" value={`${metal.shaftLengthDigit} ${metal.shaftLengthFraction}`} />}
            {isFieldVisible(module, 'isShaftLengthFinished', pageReadability) && <Tile label="Measured" value={metal.isShaftLengthFinished ? 'Finished' : 'Cut'} />}
            {isFieldVisible(module, 'shaftTippingDigit', pageReadability) && <Tile label="Tipping" value={normalizeString(`${metal.shaftTippingDigit} ${metal.shaftTippingFraction}`)} />}
            {isFieldVisible(module, 'settingName', pageReadability) && <Tile label="Setting" value={normalizeString(metal.settingName)} />}
            {isFieldVisible(module, 'sleeveName', pageReadability) && <Tile label="Sleeve" value={normalizeString(metal.sleeveName)} />}
            {isFieldVisible(module, 'shaftCosmetic', pageReadability) && <Tile label="Cosmetic" value={normalizeString(metal.shaftCosmetic)} />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'gripType', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Grip</div>
            <div className={clubStyles.categoryTitle}>{normalizeString(metal.gripType)}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'gripSize', pageReadability) && <Tile label="Grip Size" value={normalizeString(metal.gripSize)} variant="large" />}
            {isFieldVisible(module, 'rndRib', pageReadability) && <Tile label="Round/Rib" value={normalizeString(metal.rndRib)} />}
            {isFieldVisible(module, 'logo', pageReadability) && <Tile label="Logo" value={normalizeString(metal.logo)} />}
            {isFieldVisible(module, 'tape', pageReadability) && <Tile label="Grip Tape" value={normalizeString(metal.tape)} variant="large" />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'notes', pageReadability) && (
        <div>
          <div className={clubStyles.category}>
            <div className={clubStyles.notesTitle}>Notes</div>
          </div>
          <div className={clubStyles.tileRow}>
            <div className={`${clubStyles.notes} ${isEmptyTile(notes) ? clubStyles.emptyValue : ''}`}>
              <Linkify as="p" options={{ target: '_blank' }}>
                {notes}
              </Linkify>
            </div>
          </div>
        </div>
      )}
      <PhotoCaptureSection playerId={metal.playerId} ownerRowGuid={metal.rowGuid} headTypeId={metal.headTypeId} imageMetadata={metal.imageMetadata} />
    </>
  );
};

interface IMetalReadOnlyProps {
  metalClubs: TourBagDetail[];
  currentAccordionId: number;
  showCheckbox: boolean;
  /* eslint-disable no-unused-vars */
  isClubSelected: (id: number) => boolean;
  handleOpenAccordion: (id: number) => void;
  handleCheckboxEvent: (id: number, isChecked: boolean) => void;
  headTypeId: number;
  pageReadability?: PageReadability | undefined;
  /* eslint-enable no-unused-vars */
}

export const MetalReadOnly = ({ metalClubs, currentAccordionId, showCheckbox, isClubSelected, handleOpenAccordion, handleCheckboxEvent, headTypeId, pageReadability }: IMetalReadOnlyProps) => {
  return (
    <>
      {metalClubs
        ?.sort((a, b) => a.headModelName.localeCompare(b.headModelName))
        ?.sort((a, b) => Number(b.isActive) - Number(a.isActive))
        .map((metal) => {
          const laserNo = metal.laserNo !== '' ? '#' + metal.laserNo : '';
          return (
            <ControlledAccordion
              key={metal.tourBagDetailId}
              id={metal.tourBagDetailId}
              header={
                <>
                  <span className={clubStyles.model}>{pageReadability === 'limitedView' && metal.headModelAlternateName ? metal.headModelAlternateName : metal.headModelName}</span>
                  {laserNo && <span className={driverStyles.laserNo}>{laserNo}</span>}
                  <InTheBag isActive={metal.isActive} className={clubStyles.inTheBag} />
                </>
              }
              isOpen={currentAccordionId === metal.tourBagDetailId}
              showCheckbox={showCheckbox}
              checked={isClubSelected(metal.tourBagDetailId)}
              handleOpenAccordion={() => handleOpenAccordion(metal.tourBagDetailId)}
              handleCheckboxEvent={handleCheckboxEvent}
            >
              {renderMetal(metal, headTypeId, pageReadability)}
            </ControlledAccordion>
          );
        })}
    </>
  );
};

interface IMetalsProps {
  headTypeDescription: HeadTypeDescription;
}

export default function Metals({ headTypeDescription }: IMetalsProps) {
  const { tourBag, updateTourBag, player, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const [currentTourBagDetail, setCurrentTourBagDetail] = useState({} as TourBagDetail);
  const [showEditForm, setShowEditForm] = useState(false);
  const { showToast } = useToast();
  const [showDeletedHistory, setShowDeletedHistory] = useState(false);
  const [tourBagDetailList, setTourBagDetailList] = useState([] as TourBagDetail[]);
  const [clubBag, setClubBag] = useState({} as TourBagMstrPayload);
  const [showSpinner, setShowSpinner] = useState(false);
  const [editPageType, setEditPageType] = useState('');
  const pageReadability = tourPageSettings?.[headTypeDescription.toLowerCase() as keyof ITourPageSettings];
  const isFullAccess = pageReadability === 'full';
  const [showClubHistory, setShowClubHistory] = useState(false);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);

  const clubDeletedPermission = permissions?.tourBagSettings?.clubDeleted ?? false;

  useEffect(() => {
    const asyncProcess = async () => {
      let thisClubBag = tourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === headTypeDescription.toLowerCase());
      //sort by In The Bag then by date
      if (thisClubBag) {
        thisClubBag.tourBagDetail.sort((a, b) => new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
      }
      //if clubBag is empty, then no clubs have been added yet. So create a new one in case the player wants to add a new club to the collection
      if (!thisClubBag) {
        thisClubBag = createEmptyTourBag(headTypeDescription, player!);
        setTourBagDetailList([]);
      } else {
        setTourBagDetailList(thisClubBag.tourBagDetail);
      }
      setClubBag(thisClubBag);
    };
    asyncProcess();
    setShowClubHistory(false);
  }, [tourBag, headTypeDescription, player?.fullName, player?.playerId, player?.defaultTourId, player?.defaultTourName]);

  //when user clicks a new tab, cancel the edit/add new page
  useEffect(() => {
    let tourBagNew = new TourBagDetailAddNew();
    tourBagNew = {
      ...tourBagNew,
      headTypeId: headTypeMap[headTypeDescription],
      headTypeDescription: headTypeDescription,
      playerId: clubBag?.playerId || 0,
      playerName: clubBag?.playerName || '',
      tourId: player?.defaultTourId || 0,
      tourName: player?.defaultTourName || '',
    };
    setCurrentTourBagDetail(tourBagNew);
    setShowEditForm(false);
  }, [headTypeDescription, player?.playerId, clubBag.playerName, clubBag?.playerId, player?.defaultTourId, player?.defaultTourName]);

  const openOfflineEditModal = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
      setEditPageType('Edit');
      setShowPreEditOfflineModal(true);
    }
  };

  const handleEdit = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
    }
    setEditPageType('Edit');
    setShowEditForm(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    switch (editPageType) {
      case 'Edit':
        handleEdit(currentTourBagDetail.tourBagDetailId);
        break;
      case 'Add':
        openAddForm();
        break;
    }
  };

  const handleOfflineAddNew = () => {
    setEditPageType('Add');
    setShowPreEditOfflineModal(true);
  };

  const handleHistory = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
    }
    //setEditPageType('Edit');
    setShowClubHistory(true);
  };

  const openAddForm = () => {
    let tourBagNew = new TourBagDetailAddNew();
    //determine itemNo as max of other clubs
    let itemNo = 1;
    if (clubBag.tourBagDetail.length > 0) {
      itemNo = Math.max(...clubBag.tourBagDetail.map((tb) => tb.itemNo)) + 1;
    }
    tourBagNew = {
      ...tourBagNew,
      tourBagMstrId: clubBag.tourBagMstrId || tourBagNew.tourBagMstrId,
      headTypeId: headTypeMap[headTypeDescription],
      headTypeDescription: headTypeDescription,
      playerId: clubBag?.playerId || 0,
      playerName: clubBag?.playerName || '',
      tourId: player?.defaultTourId || 0,
      tourName: player?.defaultTourName || '',
      itemNo: itemNo,
    };
    setCurrentTourBagDetail(tourBagNew);
    setEditPageType('Add');
    setShowEditForm(true);
  };

  const closeEditForm = () => {
    setShowEditForm(false);
  };

  const closeClubHistoryModal = () => {
    setShowClubHistory(false);
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
    setShowSpinner(false);
  };

  const handleDeleteClub = async (tourBagDetailId: number) => {
    try {
      const tourBag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
      if (tourBag) {
        const deletedBag = createDeletedTourBag(tourBag);
        await TourBagService.saveDeletedTourBag(deletedBag);
        if (clubBag) {
          let thisDriverBag = { ...clubBag };
          thisDriverBag.tourBagDetail = thisDriverBag.tourBagDetail.filter((tb) => tb.tourBagDetailId !== tourBagDetailId);
          const action: Action = 'UPDATE';
          await updateTourBag(thisDriverBag, action, tourBagDetailId);
          showToast({ message: 'Club Deleted', showCheckmark: true });
        }
      }
    } catch {
      showToast({ message: 'Failed to Delete Club', status: 'error' });
    }
  };

  const saveEditForm = async (tourBagDetail: TourBagDetail) => {
    try {
      if (clubBag) {
        setShowSpinner(true);
        let thisDriverBag = { ...clubBag };
        const isNewItem = editPageType === 'Add' || editPageType === 'Copy';
        if (isNewItem) {
          thisDriverBag.tourBagDetail = [...thisDriverBag.tourBagDetail, tourBagDetail];
        } else {
          thisDriverBag.tourBagDetail = thisDriverBag.tourBagDetail.map((tb) => {
            return tb.tourBagDetailId == tourBagDetail.tourBagDetailId ? tourBagDetail : tb;
          });
        }
        const action: Action = isNewItem ? 'CREATE' : 'UPDATE';
        await updateTourBag(thisDriverBag, action, tourBagDetail.tourBagDetailId);
        setShowEditForm(false);
      }
      if (!isOnline) {
        //offline mode, so only show the modal
        setShowPostEditOfflineModal(true);
      } else {
        setShowSpinner(false);
        showToast({ message: 'Club Saved', showCheckmark: true, status: 'success' });
      }
    } catch {
      setShowSpinner(false);
    }
  };

  const handleCopy = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    const itemNo = Math.max(...tourBagDetailList.map((tb) => tb.itemNo)) + 1;
    if (bag) {
      let bagCopy = { ...bag, tourBagMstrId: -1, tourBagDetailId: -1, createDate: new Date(), modifyDate: new Date(), rowGuid: '', itemNo };
      setCurrentTourBagDetail(bagCopy);
      setEditPageType('Copy');
      setShowEditForm(true);
      showToast({ message: 'Club Copied', showCheckmark: true, status: 'success' });
    }
  };

  const handleShowDeletedHistory = () => {
    setShowDeletedHistory(true);
  };

  return (
    <>
      {showEditForm && <Edit headTypeId={headTypeMap[headTypeDescription]} driver={currentTourBagDetail} closeEditForm={closeEditForm} saveEditForm={saveEditForm} pageType={editPageType} />}

      {!showEditForm && (
        <div className={clubStyles.container}>
          <>
            {tourBagDetailList?.length > 0 &&
              tourBagDetailList
                .sort((a, b) => a.headModelName.localeCompare(b.headModelName))
                .sort((a, b) => Number(b.isActive) - Number(a.isActive))
                .map((metal) => {
                  const laserNo = metal.laserNo !== '' ? '#' + metal.laserNo : '';
                  return (
                    <Accordion
                      key={metal.tourBagDetailId}
                      index={metal.tourBagDetailId}
                      showMenu={isFullAccess && isSyncComplete}
                      showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                      handleEdit={!isOnline ? openOfflineEditModal : handleEdit}
                      handleDelete={() => handleDeleteClub && handleDeleteClub(metal.tourBagDetailId)}
                      handleCopy={handleCopy}
                      handleHistory={handleHistory}
                      header={
                        <>
                          <span className={clubStyles.model}>{pageReadability === 'limitedView' && metal.headModelAlternateName ? metal.headModelAlternateName : metal.headModelName}</span>
                          {laserNo && isFieldVisible(headTypeDescription, 'laserNo', pageReadability) && <span className={driverStyles.laserNo}>{laserNo}</span>}
                          <InTheBag isActive={metal.isActive} className={clubStyles.inTheBag} />
                        </>
                      }
                    >
                      {renderMetal(metal, headTypeMap[headTypeDescription], pageReadability)}
                    </Accordion>
                  );
                })}
            {tourBagDetailList?.length == 0 && <div className={clubStyles.noClubs}>No {headTypeDescription}s in System</div>}
            {isFullAccess && isSyncComplete && (
              <div className={clubStyles.addNewWrapper}>
                <button className={`${clubStyles.addNew} ${driverStyles.addNew}`} onClick={!isOnline ? handleOfflineAddNew : openAddForm} aria-label="Add New Metal">
                  + Add New
                </button>
                {isOnline && clubDeletedPermission && (
                  <button className={clubStyles.addNew} onClick={handleShowDeletedHistory} aria-label="Show Deleted Metals">
                    Show Deleted
                  </button>
                )}
              </div>
            )}
            {showDeletedHistory && (
              <DeletedHistory
                headTypeId={headTypeMap[headTypeDescription]}
                playerId={clubBag?.playerId || 0}
                playerName={clubBag?.playerName || ''}
                showDeletedHistory={showDeletedHistory}
                closeDeletedHistory={() => setShowDeletedHistory(false)}
              />
            )}
          </>
        </div>
      )}
      {showClubHistory && (
        <ClubHistory
          tourBagMstrId={currentTourBagDetail.tourBagMstrId}
          rowGuid={currentTourBagDetail.rowGuid}
          headTypeId={headTypeMap[headTypeDescription]}
          player={player}
          model={currentTourBagDetail.headModelName}
          closeModal={closeClubHistoryModal}
        />
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal
        onCancel={() => {
          setShowPreEditOfflineModal(false);
        }}
        onClose={showEditFormOfflineModal}
        isOpen={showPreEditOfflineModal}
        modalType={'IsOffline'}
      />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </>
  );
}
